import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ttsService } from '../services/ttsService';
import { BTN_SECONDARY } from '../lib/buttonStyles';
import { useIsMobile } from '../hooks/useIsMobile';

const POLL_INTERVAL = 1000;

// Текст кнопки «Пример» — из артборда «Бумаги», дословно: в нём нарочно есть
// дата, время, процент, кавычки и аббревиатура, то есть ровно то, что правит
// адаптация под чтение. Поэтому она вместе с примером и включается.
const EXAMPLE = 'Добрый день! Встреча отдела продаж пройдёт 5 сентября в 10:00. '
  + 'Обсудим итоги августа: план выполнен на 94%, КП для ООО «Вектор» готово.';

const TTSPage = () => {
  const [text, setText] = useState('');
  const [voices, setVoices] = useState({ system: [], user: [] });
  // Поиск по голосам и раскрытая группа: список из 36 строк иначе не читается.
  const [openGroup, setOpenGroup] = useState(null);
  const [selectedVoice, setSelectedVoice] = useState('default');
  // Телефон: голос — строкой «Голос: имя ▾», список раскрывается по нажатию и
  // сворачивается выбором. Раскрытым он занимал полэкрана (тест владельца 27.09).
  const isMobile = useIsMobile();
  const [voicePickerOpen, setVoicePickerOpen] = useState(false);
  const [temperature, setTemperature] = useState(0.7);
  const [rate, setRate] = useState('medium');
  // Silero обслуживает базовый голос и всех именованных дикторов ru_*.
  const isSileroVoice = selectedVoice === 'default' || selectedVoice.startsWith('ru_');
  const [audioFormat, setAudioFormat] = useState('wav');
  const [adaptText, setAdaptText] = useState(false);
  const [preparedPreview, setPreparedPreview] = useState('');
  // Правка человека в подготовленном тексте. Пока null — показываем то,
  // что собрал сервис; как только поправили — синтез берёт именно её.
  const [preparedEdited, setPreparedEdited] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [status, setStatus] = useState(null);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [taskId, setTaskId] = useState(null);
  const pollRef = useRef(null);
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [position, setPosition] = useState(0);
  const [duration, setDuration] = useState(0);

  const preparedRef = useRef(null);
  const sourceRef = useRef(null);
  const audioUrlRef = useRef(null);

  // Загрузка голосов при монтировании
  useEffect(() => {
    ttsService.getVoices()
      .then(setVoices)
      .catch((err) => console.error('Failed to load voices:', err));
  }, []);

  // Очистка при размонтировании
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (audioUrlRef.current) URL.revokeObjectURL(audioUrlRef.current);
    };
  }, []);

  // Превью адаптации текста (debounce 400мс)
  useEffect(() => {
    if (!adaptText || !text.trim()) {
      setPreparedPreview('');
      return;
    }
    const handle = setTimeout(() => {
      ttsService.previewAdapt(text)
        .then(({ prepared }) => {
          setPreparedPreview(prepared || '');
          // Правка исходного текста отменяет ручную: иначе она молча устареет.
          setPreparedEdited(null);
        })
        .catch(() => setPreparedPreview(''));
    }, 400);
    return () => clearTimeout(handle);
  }, [text, adaptText]);

  // Поле подготовленного текста растёт под содержимое: протокол на несколько
  // тысяч знаков в три строки не помещается. Потолок — чтобы страница не
  // превращалась в один бесконечный textarea; дальше прокрутка внутри поля.
  useEffect(() => {
    const grow = (el, max) => {
      if (!el) return;
      el.style.height = 'auto';
      el.style.height = `${Math.min(el.scrollHeight, max)}px`;
    };
    grow(sourceRef.current, 420);
    grow(preparedRef.current, 420);
  }, [text, preparedPreview, preparedEdited]);

  const pollStatus = useCallback((id) => {
    pollRef.current = setInterval(async () => {
      try {
        const data = await ttsService.getStatus(id);
        setStatus(data.status);
        if (typeof data.progress === 'number') setProgress(data.progress);

        if (data.status === 'READY') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          try {
            if (audioUrlRef.current) URL.revokeObjectURL(audioUrlRef.current);
            const url = await ttsService.getFile(id);
            audioUrlRef.current = url;
            setAudioUrl(url);
          } catch (err) {
            setError('Ошибка загрузки аудио');
          }
          setIsProcessing(false);
        } else if (data.status === 'ERROR') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setIsProcessing(false);
          setError(data.error || 'Ошибка синтеза речи');
        }
      } catch (err) {
        console.error('Poll error:', err);
      }
    }, POLL_INTERVAL);
  }, []);

  const handleSynthesize = async () => {
    if (!text.trim()) return;

    setIsProcessing(true);
    setError(null);
    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current);
      audioUrlRef.current = null;
    }
    setAudioUrl(null);
    setStatus('CREATED');
    setProgress(0);
    setTaskId(null);

    try {
      const data = await ttsService.synthesize(
        text, selectedVoice, audioFormat, temperature, adaptText, rate, preparedEdited);
      setTaskId(data.task_id);
      pollStatus(data.task_id);
    } catch (err) {
      setIsProcessing(false);
      setError(err.response?.data?.detail || 'Ошибка при отправке запроса');
    }
  };

  const handleDownload = async () => {
    if (!taskId) return;
    try {
      const url = await ttsService.getFile(taskId);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tts_${taskId.slice(0, 8)}.${audioFormat}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError('Ошибка скачивания файла');
    }
  };

  const handleCancel = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    setIsProcessing(false);
    setStatus(null);
    setProgress(0);
    setTaskId(null);
    setError(null);
  };

  // Удаление своего голоса прямо из списка: в макете у клонированного голоса
  // стоит «×». Раньше это жило только в диалоге «Мои голоса».
  const handleDeleteVoice = async (voiceId, e) => {
    e.stopPropagation();
    try {
      await ttsService.deleteVoice(voiceId);
      handleVoiceDeleted(voiceId);
    } catch (_) {
      setError('Не удалось удалить голос');
    }
  };

  // «Новая озвучка» из макета: убирает результат, оставляя текст и настройки —
  // человек чаще всего правит одну фразу и озвучивает снова, а не начинает с нуля.
  const handleReset = () => {
    setAudioUrl(null);
    setTaskId(null);
    setError(null);
    setProgress(0);
    setStatus(null);
  };

  const statusText = {
    CREATED: 'Ожидаем свободный GPU…',
    PENDING: 'Ожидаем свободный GPU…',
    PROCESSING: 'Генерация аудио…',
  };

  const ctaLabel = status === 'PROCESSING' ? 'Синтез' : 'В очереди';

  // Пять делений темпа, подписи короткие — они стоят чипами в узкой колонке.
  const RATES = [
    ['x-slow', '½×', 'очень медленно'],
    ['slow', '¾×', 'медленно'],
    ['medium', '1×', 'обычно'],
    ['fast', '1¼×', 'быстро'],
    ['x-fast', '1½×', 'очень быстро'],
  ];

  // Плеер результата собран по артборду: круглая кнопка, имя «голос · N слов»,
  // позиция/длительность и волна из 60 полосок. Волна там не настоящая амплитуда,
  // а формула — переносим её как есть, чтобы картинка совпадала с макетом.
  const WAVE = Array.from({ length: 60 }, (_, i) => (
    8 + Math.round(Math.abs(Math.sin(i * 0.9) * 14 + Math.cos(i * 0.37) * 8))
  ));

  const mmss = (sec) => {
    if (!Number.isFinite(sec)) return '0:00';
    const m = Math.floor(sec / 60);
    return `${m}:${String(Math.floor(sec % 60)).padStart(2, '0')}`;
  };

  const voiceName = (
    voices.system.concat(voices.user).find((v) => v.id === selectedVoice) || {}
  ).name || 'Голос';

  const wordCount = text.split(/\s+/).filter(Boolean).length;
  const played = duration > 0 ? position / duration : 0;

  const togglePlay = () => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) el.play(); else el.pause();
  };

  const seek = (e) => {
    const el = audioRef.current;
    if (!el || !duration) return;
    const box = e.currentTarget.getBoundingClientRect();
    el.currentTime = duration * Math.min(1, Math.max(0, (e.clientX - box.left) / box.width));
  };

  // Голоса одним списком с подписями групп — как в артборде. Дикторы отделены от
  // базовых по префиксу `ru_`, свои несут процент совпадения и «×».
  //
  // 🪤 Артборд рисует пять голосов и закрывает вопрос прокруткой панели. В проде
  // их 36 — два базовых, 29 дикторов и до пяти своих, — и плоский список тянется
  // на полтора экрана (найдено владельцем 07.09.2026). Поэтому группы
  // сворачиваются: раскрыта та, где стоит выбранный голос, у остальных видно
  // только имя и счётчик. Поиск отменяет сворачивание — ищем по всем сразу.
  //
  // 🔴 Клонированных голосов в продукте нет (решение Владимира 24.09: «попадает
  // под законы»): ни своих (`usr_*`), ни «Бурунова» — это клон голоса живого
  // человека. Показываем только Silero — базовый и дикторов. Свои голоса,
  // записанные раньше, остаются в bit-tts, но из интерфейса недоступны.
  const voiceGroups = [
    { label: 'Базовые', items: voices.system.filter((v) => v.id === 'default') },
    { label: 'Дикторы', items: voices.system.filter((v) => v.id.startsWith('ru_')) },
  ].filter((g) => g.items.length > 0);

  const pickVoice = (id) => {
    setSelectedVoice(id);
    setPreparedEdited(null);
    setVoicePickerOpen(false);
  };

  // Поиска по голосам нет (решение владельца 27.09): набор фиксированный, имена
  // дикторов случайные — искать по ним нечего, группы проще пролистать.
  const foundGroups = voiceGroups.filter((g) => g.items.length > 0);
  // Раскрытая группа: выбор человека, а по умолчанию — та, где стоит его голос.
  const groupOfSelected = voiceGroups.find((g) => g.items.some((v) => v.id === selectedVoice));
  const expandedGroup = openGroup !== null ? openGroup : (groupOfSelected ? groupOfSelected.label : null);
  const isIndeterminate = status === 'CREATED' || status === 'PENDING';

  const handleVoiceDeleted = (voiceId) => {
    ttsService.getVoices().then((data) => {
      setVoices(data);
      if (selectedVoice === voiceId) setSelectedVoice('default');
    });
  };


  const LABEL = 'font-mono text-[11px] uppercase tracking-[0.1em] text-paper-ink-3';
  const BTN = 'rounded-[10px] px-[14px] py-[9px] text-[13px] font-semibold transition-colors';
  const BTN_QUIET = `${BTN} bg-paper-bg text-paper-ink hover:bg-paper-panel`;

  return (
    // Телефон: листается вся страница. Растянутая на высоту экрана сетка сжимала
    // раскрытый список голосов почти до нуля — выбрать голос было негде (27.09).
    <div className="shrink-0 md:shrink md:flex-1 md:min-h-0">
      {/* Сетка артборда: рабочая область целиком, 1fr + 332px. Ширину не
          ограничиваем — в «Бумаге» ни один экран не сужен, и поля по бокам на
          широком мониторе были моей отсебятиной (найдено владельцем 07.09.2026). */}
      <div className="md:h-full grid gap-3 grid-cols-[minmax(0,1fr)] lg:grid-cols-[minmax(0,1fr)_332px] items-stretch">
        {/* ── Текст, ход синтеза и результат ────────────────────────
            Верстка по артборду: карточка текста без рамок и без заливки у поля,
            действия и счётчик — строкой в шапке карточки. */}
        <div className="flex flex-col gap-3 min-h-0">
          <section className="flex-1 min-h-0 bg-paper-card rounded-card px-5 pt-5 pb-4 md:px-[30px] md:pt-[26px] md:pb-[18px] flex flex-col">
            <div className="flex items-baseline justify-between gap-3">
              <div className={LABEL}>Текст</div>
              <div className="flex items-baseline gap-[14px] text-[12px] text-paper-ink-3">
                {text
                  ? (
                    <button type="button" onClick={() => setText('')} disabled={isProcessing}
                            className="text-paper-ink underline underline-offset-[3px]">
                      Очистить
                    </button>
                  )
                  : (
                    <button type="button" onClick={() => { setText(EXAMPLE); setAdaptText(true); setPreparedEdited(null); }} disabled={isProcessing}
                            className="text-paper-ink underline underline-offset-[3px]">
                      Пример
                    </button>
                  )}
                <span className="font-mono">{text.length} / 5000</span>
              </div>
            </div>

            <textarea
              ref={sourceRef}
              className="w-full flex-1 min-h-[160px] mt-4 bg-transparent border-0 outline-none resize-none text-[17px] leading-[1.6] text-paper-ink placeholder:text-paper-hint"
              placeholder="Вставьте или напишите текст — до 5000 знаков. Числа, аббревиатуры и «ё» поправим сами, если включить адаптацию."
              value={text}
              onChange={(e) => setText(e.target.value)}
              maxLength={5000}
              disabled={isProcessing}
            />

            {/* Единственный предпросмотр: то, что реально уйдёт в синтез. Поле
                редактируемое — знак + перед гласной ставится руками там, где машина
                ошиблась на фамилии, термине или омографе. Правка исходного текста
                выше пересобирает подготовку заново и отменяет ручную версию. */}
            {isSileroVoice && preparedPreview && (
              <div className="shrink-0 mt-[14px] bg-paper-panel rounded-[12px] px-4 py-[14px]">
                <div className="flex justify-between items-baseline gap-3 mb-2">
                  <span className="text-[12px] text-paper-ink-2">
                    Так это прочитает голос. Ударение —{' '}
                    <b className="font-mono font-medium text-paper-ink">+</b> перед гласной, можно поправить.
                  </span>
                  {preparedEdited !== null && (
                    <button type="button" onClick={() => setPreparedEdited(null)} disabled={isProcessing}
                            className="shrink-0 whitespace-nowrap text-[12px] underline underline-offset-[3px]">
                      вернуть автоматический
                    </button>
                  )}
                </div>
                <textarea
                  value={preparedEdited !== null ? preparedEdited : preparedPreview}
                  onChange={(e) => setPreparedEdited(e.target.value)}
                  disabled={isProcessing}
                  ref={preparedRef}
                  rows={3}
                  className="w-full bg-transparent border-0 outline-none resize-none font-mono text-[13px] leading-[1.6] text-paper-ink"
                />
                {preparedEdited !== null && (
                  <div className="text-[12px] text-paper-ink-3 mt-1">
                    Синтез возьмёт этот текст как есть.
                  </div>
                )}
              </div>
            )}
          </section>

          {isProcessing && (
            <section className="bg-paper-card rounded-card px-[30px] py-[22px] flex items-center justify-between gap-6 shrink-0">
              <div>
                <div className="text-[12px] text-paper-ink-3">{statusText[status] || 'Обработка…'}</div>
                <div className="font-display text-[28px] font-extrabold tracking-[-0.03em] leading-none mt-[6px]">
                  Озвучиваем <span className="text-paper-ink-3">· {voiceName}</span>
                </div>
              </div>
              <div className="w-[220px] flex items-center gap-[14px]">
                <div className="flex-1 h-[6px] rounded-[3px] bg-paper-bg overflow-hidden">
                  {isIndeterminate ? (
                    <div className="h-full w-[30%] bg-paper-accent animate-tts-indet" />
                  ) : (
                    <div className="h-full bg-paper-accent transition-[width] duration-300"
                         style={{ width: `${progress}%` }} />
                  )}
                </div>
                <button type="button" onClick={handleCancel} className={BTN_SECONDARY}>
                  Отменить
                </button>
              </div>
            </section>
          )}

          {audioUrl && (
            <section className="bg-paper-card rounded-card px-6 py-5 flex items-center gap-[18px] shrink-0">
              <audio
                ref={audioRef}
                src={audioUrl}
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
                onTimeUpdate={(e) => setPosition(e.currentTarget.currentTime)}
                onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
                className="hidden"
              />
              <button
                type="button"
                onClick={togglePlay}
                aria-label={playing ? 'Пауза' : 'Слушать'}
                className="w-[52px] h-[52px] rounded-full bg-paper-accent hover:bg-paper-accent-hover text-white flex items-center justify-center shrink-0"
              >
                {playing ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <rect x="6" y="4" width="4" height="16" rx="1" />
                    <rect x="14" y="4" width="4" height="16" rx="1" />
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" style={{ marginLeft: 3 }}>
                    <path d="M6 4l14 8-14 8z" />
                  </svg>
                )}
              </button>

              <div className="flex-1 min-w-0">
                <div className="flex justify-between text-[13px] mb-2">
                  <span className="font-semibold text-paper-ink truncate">{voiceName} · {wordCount} слов</span>
                  <span className="font-mono text-paper-ink-3 shrink-0 ml-3">
                    {mmss(position)} / {mmss(duration)}
                  </span>
                </div>
                <div onClick={seek} role="presentation"
                     className="flex items-center gap-[2px] h-8 cursor-pointer">
                  {WAVE.map((h, i) => (
                    <span
                      key={i}
                      className="flex-1 rounded-[2px]"
                      style={{ height: h, background: i / 60 <= played ? '#E50071' : '#E4E3DE' }}
                    />
                  ))}
                </div>
              </div>

              <div className="flex flex-col gap-[6px] shrink-0">
                <button type="button" onClick={handleDownload}
                        className="rounded-[10px] px-[14px] py-[9px] text-[13px] font-semibold bg-paper-ink text-white">
                  Скачать .{audioFormat}
                </button>
                <button type="button" onClick={handleReset}
                        className="rounded-[10px] px-[14px] py-2 text-[13px] font-medium bg-paper-bg text-paper-ink">
                  Новая озвучка
                </button>
              </div>
            </section>
          )}

          {error && (
            <div className="rounded-panel bg-paper-accent/[0.06] text-paper-accent p-4 text-[13px]">{error}</div>
          )}
        </div>

        {/* ── Голос и настройки ─────────────────────────────────────── */}
        {/* Верстка по артборду: голоса — список кнопок с точкой, процентом и «×»,
            а не выпадающий список; темп и формат — чипы; метки настроек стоят
            колонкой 72px слева. Нативные select и checkbox сюда не годятся: у них
            своя рамка и своя стрелка, а в «Бумаге» у контролов есть только заливка. */}
        {/* kbd-hide: пока печатают текст, голос и настройки убраны — остаётся
            поле. Поиск голоса живёт внутри колонки, и она при нём остаётся. */}
        <div className="kbd-hide flex flex-col gap-3 md:min-h-0">
          <section className="md:min-h-0 bg-paper-card rounded-panel px-5 py-[18px] flex flex-col gap-[14px]">
            <div className={LABEL}>Голос</div>
            {isMobile && !voicePickerOpen ? (
              <button type="button" onClick={() => setVoicePickerOpen(true)} disabled={isProcessing}
                      data-testid="tts-voice-toggle"
                      className="-mt-1 min-h-11 flex items-center gap-2 rounded-[10px] bg-paper-bg px-3 text-left">
                <span className="w-2 h-2 rounded-full shrink-0 bg-paper-accent" />
                <span className="flex-1 min-w-0 truncate text-[15px] font-medium text-paper-ink">{voiceName}</span>
                <span aria-hidden="true" className="text-paper-ink-3">▾</span>
              </button>
            ) : (
            <>

            <div className="flex flex-col gap-[2px] min-h-0 max-h-[55vh] md:max-h-none overflow-y-auto">
              {foundGroups.map(({ label, items }) => {
                const open = expandedGroup === label;
                return (
                  <React.Fragment key={label}>
                    <button
                      type="button"
                      onClick={() => setOpenGroup(open ? '' : label)}
                      className="flex items-baseline gap-2 rounded-[8px] px-[10px] pt-[10px] pb-1 max-md:min-h-11 max-md:items-center max-md:pt-1 text-left"
                    >
                      <span className="text-[11px] text-paper-ink-3">{label}</span>
                      <span className="font-mono text-[11px] text-paper-hint">{items.length}</span>
                      <span className="ml-auto text-[10px] text-paper-hint">{open ? '\u25be' : '\u25b8'}</span>
                    </button>

                    {open && items.map((v) => {
                      const active = v.id === selectedVoice;
                      return (
                        <button
                          key={v.id}
                          type="button"
                          disabled={isProcessing}
                          onClick={() => pickVoice(v.id)}
                          className={`flex items-center gap-[10px] rounded-[10px] px-[10px] py-2 max-md:min-h-11 text-left
                                      ${active ? 'bg-paper-panel' : 'hover:bg-paper-panel'}`}
                        >
                          <span className={`w-2 h-2 rounded-full shrink-0
                                            ${active ? 'bg-paper-accent' : 'bg-paper-track'}`} />
                          <span className="flex-1 text-[14px] font-medium text-paper-ink truncate">{v.name}</span>
                          {v.similarity_pct != null && (
                            <span className="font-mono text-[11px] text-paper-ink-3" title="Совпадение с вашим голосом">
                              {v.similarity_pct}%
                            </span>
                          )}
                          {v.mine && (
                            <span
                              role="button"
                              tabIndex={0}
                              title="Удалить голос"
                              onClick={(e) => handleDeleteVoice(v.id, e)}
                              onKeyDown={(e) => e.key === 'Enter' && handleDeleteVoice(v.id, e)}
                              className="px-[2px] text-[14px] text-paper-hint hover:text-paper-accent"
                            >
                              ×
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </React.Fragment>
                );
              })}

            </div>
            </>
            )}
          </section>

          <section className="bg-paper-card rounded-panel px-5 py-[18px] flex flex-col gap-[14px]">
            <div className="grid grid-cols-[72px_1fr] gap-x-3 gap-y-[10px] items-center text-[13px]">
              {/* Темп — для голосов Silero (базовый и дикторы). «Живость» там не
                  работает: модель детерминирована, параметра температуры у неё нет. */}
              {isSileroVoice ? (
                <>
                  <span className={LABEL}>Темп</span>
                  <div className="flex gap-[3px]">
                    {RATES.map(([value, label, tip]) => (
                      <button
                        key={value}
                        type="button"
                        title={tip}
                        disabled={isProcessing}
                        onClick={() => setRate(value)}
                        className={`flex-1 rounded-[7px] py-[5px] text-[12px] font-medium transition-colors ${
                          rate === value ? 'bg-paper-ink text-white' : 'bg-paper-bg text-paper-ink-2'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <>
                  <span className={LABEL}>Живость</span>
                  <div className="flex items-center gap-[10px]">
                    <input
                      type="range" min="0.1" max="1.0" step="0.1"
                      value={temperature}
                      onChange={(e) => setTemperature(parseFloat(e.target.value))}
                      disabled={isProcessing}
                      className="flex-1 accent-paper-accent"
                    />
                    <span className="font-mono text-[12px] w-6 text-right text-paper-ink">
                      {temperature.toFixed(1)}
                    </span>
                  </div>
                </>
              )}

              <span className={LABEL}>Формат</span>
              <div className="flex gap-[3px]">
                {['wav', 'mp3'].map((fmt) => (
                  <button
                    key={fmt}
                    type="button"
                    disabled={isProcessing}
                    onClick={() => setAudioFormat(fmt)}
                    className={`rounded-[7px] px-3 py-[5px] text-[12px] font-medium transition-colors ${
                      audioFormat === fmt ? 'bg-paper-ink text-white' : 'bg-paper-bg text-paper-ink-2'
                    }`}
                  >
                    {fmt.toUpperCase()}
                  </button>
                ))}
              </div>

              <span className={LABEL}>Текст</span>
              <button
                type="button"
                disabled={isProcessing}
                onClick={() => setAdaptText((v) => !v)}
                title="Восстановит ё, числа — словами, расшифрует аббревиатуры"
                className="flex items-center gap-2 text-[13px] text-paper-ink text-left"
              >
                <span className={`w-4 h-4 rounded-[4px] border-[1.5px] border-paper-ink inline-flex
                                  items-center justify-center shrink-0
                                  ${adaptText ? 'bg-paper-ink' : 'bg-transparent'}`}>
                  {adaptText && (
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff"
                         strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 6 9 17l-5-5" />
                    </svg>
                  )}
                </span>
                Адаптировать под чтение
              </button>
            </div>

            {isProcessing ? (
              <div className="flex gap-2">
                <button type="button" disabled
                        className="flex-1 rounded-control py-[14px] text-[14px] font-semibold bg-paper-bg text-paper-hint">
                  {ctaLabel}…
                </button>
                <button type="button" className={BTN_QUIET} onClick={handleCancel}>Отменить</button>
              </div>
            ) : (
              <button
                type="button"
                onClick={handleSynthesize}
                disabled={!text.trim()}
                className="rounded-control py-[14px] text-[14px] font-semibold bg-paper-accent text-white hover:bg-paper-accent-hover disabled:bg-paper-bg disabled:text-paper-hint transition-colors"
              >
                Озвучить
              </button>
            )}
          </section>
        </div>
      </div>

    </div>
  );
};

export default TTSPage;
